import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import VersionHistoryCopyAppBar from '../../../../common/VersionHistoryCopyAppBar';
import DocumentHeader from '../../../Documents/components/DocumentHeader';
import MoveNodesRenderer from './MoveNodesRenderer';
import { setMovePosition } from '../../../../redux/previewSlice';
import Loader from '../../../../common/Loader';

/**
 * MovePositionSelector Component
 * Reuses logic from PastePositionSelector but adapted for move operations
 * Allows user to select where to move the node
 * For same-document moves: shows current document structure
 * For cross-document moves: shows target document structure
 */
const MovePositionSelector = ({ onPositionSelected, onCancel, createCopy, onCreateCopyChange }) => {
  const dispatch = useDispatch();
  const { moveNode, moveDocument, moveTargetDocument, moveOriginalData, moveIsLoading } = useSelector(state => state.preview);
  
  // The document to show for position selection (target if cross-doc, otherwise source)
  const selectionDocument = moveTargetDocument || moveDocument;
  
  // Selection state: 'documentHeader' | 'section' | 'step' | 'substep' | 'child' | null
  const [selectedType, setSelectedType] = useState(null);
  const [selectedSectionId, setSelectedSectionId] = useState(null);
  const [selectedSectionIndex, setSelectedSectionIndex] = useState(null);
  const [selectedStepId, setSelectedStepId] = useState(null);
  const [selectedStepIndex, setSelectedStepIndex] = useState(null);
  const [selectedGlobalStepNumber, setSelectedGlobalStepNumber] = useState(null);
  const [selectedSubstepId, setSelectedSubstepId] = useState(null);
  const [selectedSubstepIndex, setSelectedSubstepIndex] = useState(null);
  const [selectedSubstepLetter, setSelectedSubstepLetter] = useState(null);
  const [selectedChildId, setSelectedChildId] = useState(null);
  const [selectedChildIndex, setSelectedChildIndex] = useState(null);
  const [selectedChildType, setSelectedChildType] = useState(null);
  const [selectedPosition, setSelectedPosition] = useState(null); // 'above' | 'below' | null
  
  // Track DocumentHeader checkbox state
  const isDocumentHeaderChecked = selectedType === 'documentHeader';


  // Handle DocumentHeader checkbox click
  const handleDocumentHeaderCheckbox = () => {
    if (selectedType === 'documentHeader') {
      setSelectedType(null);
    } else {
      setSelectedType('documentHeader');
      setSelectedSectionId(null);
      setSelectedSectionIndex(null);
    }
  };

  // Handle section checkbox click
  const handleSectionCheckbox = (sectionId, sectionIndex) => {
    // Sections should only be selectable for section and step moves, not for substeps or child nodes
    if (moveNode?.nodeType === 'substep' || ['attachment','video','rich-text','form','canva','canvas'].includes(moveNode?.nodeType)) {
      return; // Do nothing for substeps or child nodes
    }
    
    if (selectedType === 'section' && selectedSectionId === sectionId) {
      setSelectedType(null);
      setSelectedSectionId(null);
      setSelectedSectionIndex(null);
    } else {
      setSelectedType('section');
      setSelectedSectionId(sectionId);
      setSelectedSectionIndex(sectionIndex);
      // Clear other selections
      if (moveNode?.nodeType === 'step' || moveNode?.nodeType === 'substep') {
        setSelectedStepId(null);
        setSelectedStepIndex(null);
        setSelectedGlobalStepNumber(null);
      }
    }
  };

  // Handle step checkbox click
  const handleStepCheckbox = (stepId, stepIndex, globalStepNumber, sectionId) => {
    const isChildMove = ['attachment','video','rich-text','form','canva','canvas'].includes(moveNode?.nodeType);
    if (moveNode?.nodeType === 'step' || moveNode?.nodeType === 'substep' || isChildMove) {
      if (selectedType === 'step' && selectedStepId === stepId) {
        setSelectedType(null);
        setSelectedStepId(null);
        setSelectedStepIndex(null);
        setSelectedGlobalStepNumber(null);
      } else {
        setSelectedType('step');
        setSelectedStepId(stepId);
        setSelectedStepIndex(stepIndex);
        setSelectedGlobalStepNumber(globalStepNumber);
        const section = moveOriginalData?.find(s => s.steps?.some(st => st.tree_node_id === stepId));
        if (section) {
          const sectionIndex = moveOriginalData.findIndex(s => s.tree_node_id === section.tree_node_id);
          setSelectedSectionId(section.tree_node_id);
          setSelectedSectionIndex(sectionIndex);
        }
        setSelectedSubstepId(null);
        setSelectedSubstepIndex(null);
        setSelectedSubstepLetter(null);
      }
    }
  };

  // Handle substep checkbox click
  const handleSubstepCheckbox = (substepId, substepIndex, substepLetter, globalStepNumber, stepId, sectionId) => {
    const isChildMove = ['attachment','video','rich-text','form','canva','canvas'].includes(moveNode?.nodeType);
    if (moveNode?.nodeType === 'substep' || isChildMove) {
      if (selectedType === 'substep' && selectedSubstepId === substepId) {
        setSelectedType(null);
        setSelectedSubstepId(null);
        setSelectedSubstepIndex(null);
        setSelectedSubstepLetter(null);
      } else {
        setSelectedType('substep');
        setSelectedSubstepId(substepId);
        setSelectedSubstepIndex(substepIndex);
        setSelectedSubstepLetter(substepLetter);
        setSelectedStepId(stepId);
        setSelectedGlobalStepNumber(globalStepNumber);
        const section = moveOriginalData?.find(s => s.tree_node_id === sectionId);
        if (section) {
          const sectionIndex = moveOriginalData.findIndex(s => s.tree_node_id === section.tree_node_id);
          setSelectedSectionId(section.tree_node_id);
          setSelectedSectionIndex(sectionIndex);
        }
        setSelectedChildId(null);
        setSelectedChildIndex(null);
        setSelectedChildType(null);
      }
    }
  };

  // Handle child checkbox click
  const handleChildCheckbox = (childId, childType, childIndex, globalStepNumber, stepId, sectionId, substepId) => {
    const isChildMove = ['attachment','video','rich-text','form','canva','canvas'].includes(moveNode?.nodeType);
    if (!isChildMove) return;
    if (selectedType === 'child' && selectedChildId === childId) {
      setSelectedType(null);
      setSelectedChildId(null);
      setSelectedChildIndex(null);
      setSelectedChildType(null);
      setSelectedPosition(null);
    } else {
      setSelectedType('child');
      setSelectedChildId(childId);
      setSelectedChildIndex(childIndex);
      setSelectedChildType(childType);
      setSelectedStepId(stepId);
      setSelectedGlobalStepNumber(globalStepNumber);
      setSelectedPosition(null); // Reset position when selecting a new child
      const section = moveOriginalData?.find(s => s.tree_node_id === sectionId);
      if (section) {
        const sectionIndex = moveOriginalData.findIndex(s => s.tree_node_id === section.tree_node_id);
        setSelectedSectionId(section.tree_node_id);
        setSelectedSectionIndex(sectionIndex);
      }
      setSelectedSubstepId(substepId || null);
    }
  };

  // Handle Move Into button
  const handleMoveInto = () => {
    // Prevent action if loading
    if (moveIsLoading) return;
    
    if (selectedType === 'documentHeader') {
      dispatch(setMovePosition({
        position: 'into',
        targetType: 'document',
        targetId: null,
        targetIndex: null
      }));
      if (onPositionSelected) {
        onPositionSelected('into', 'document', null, null);
      }
    } else if (selectedType === 'section' && (moveNode?.nodeType === 'step' || moveNode?.nodeType === 'substep')) {
      dispatch(setMovePosition({
        position: 'into',
        targetType: 'section',
        targetId: selectedSectionId,
        targetIndex: selectedSectionIndex
      }));
      if (onPositionSelected) {
        onPositionSelected('into', 'section', selectedSectionId, selectedSectionIndex);
      }
    } else if (selectedType === 'step' && moveNode?.nodeType === 'substep') {
      dispatch(setMovePosition({
        position: 'into',
        targetType: 'step',
        targetId: selectedStepId,
        targetIndex: selectedStepIndex,
        globalStepNumber: selectedGlobalStepNumber,
        sectionId: selectedSectionId,
        sectionIndex: selectedSectionIndex
      }));
      if (onPositionSelected) {
        onPositionSelected('into', 'step', selectedStepId, selectedStepIndex, selectedGlobalStepNumber, selectedSectionId, selectedSectionIndex);
      }
    } else if (selectedType === 'step' && ['attachment','video','rich-text','form','canva','canvas'].includes(moveNode?.nodeType)) {
      dispatch(setMovePosition({
        position: 'into',
        targetType: 'step',
        targetId: selectedStepId,
        targetIndex: selectedStepIndex,
        globalStepNumber: selectedGlobalStepNumber,
        sectionId: selectedSectionId,
        sectionIndex: selectedSectionIndex
      }));
      if (onPositionSelected) {
        onPositionSelected('into', 'step', selectedStepId, selectedStepIndex, selectedGlobalStepNumber, selectedSectionId, selectedSectionIndex);
      }
    } else if (selectedType === 'substep' && ['attachment','video','rich-text','form','canva','canvas'].includes(moveNode?.nodeType)) {
      dispatch(setMovePosition({
        position: 'into',
        targetType: 'substep',
        targetId: selectedSubstepId,
        targetIndex: selectedSubstepIndex,
        globalStepNumber: selectedGlobalStepNumber,
        sectionId: selectedSectionId,
        sectionIndex: selectedSectionIndex,
        stepId: selectedStepId
      }));
      if (onPositionSelected) {
        onPositionSelected('into', 'substep', selectedSubstepId, selectedSubstepIndex, selectedGlobalStepNumber, selectedSectionId, selectedSectionIndex);
      }
    }
  };

  // Handle Move Above button
  const handleMoveAbove = () => {
    // Prevent action if loading
    if (moveIsLoading) return;
    
    if (selectedType === 'section' && moveNode?.nodeType === 'section') {
      dispatch(setMovePosition({
        position: 'above',
        targetType: 'section',
        targetId: selectedSectionId,
        targetIndex: selectedSectionIndex
      }));
      if (onPositionSelected) {
        onPositionSelected('above', 'section', selectedSectionId, selectedSectionIndex);
      }
    } else if (selectedType === 'step' && moveNode?.nodeType === 'step') {
      dispatch(setMovePosition({
        position: 'above',
        targetType: 'step',
        targetId: selectedStepId,
        targetIndex: selectedStepIndex,
        globalStepNumber: selectedGlobalStepNumber,
        sectionId: selectedSectionId,
        sectionIndex: selectedSectionIndex
      }));
      if (onPositionSelected) {
        onPositionSelected('above', 'step', selectedStepId, selectedStepIndex, selectedGlobalStepNumber, selectedSectionId, selectedSectionIndex);
      }
    } else if (selectedType === 'substep' && moveNode?.nodeType === 'substep') {
      dispatch(setMovePosition({
        position: 'above',
        targetType: 'substep',
        targetId: selectedSubstepId,
        targetIndex: selectedSubstepIndex,
        globalStepNumber: selectedGlobalStepNumber,
        sectionId: selectedSectionId,
        sectionIndex: selectedSectionIndex,
        stepId: selectedStepId,
        substepLetter: selectedSubstepLetter
      }));
      if (onPositionSelected) {
        onPositionSelected('above', 'substep', selectedSubstepId, selectedSubstepIndex, selectedGlobalStepNumber, selectedSectionId, selectedSectionIndex);
      }
    } else if (selectedType === 'child' && ['attachment','video','rich-text','form','canva','canvas'].includes(moveNode?.nodeType)) {
      setSelectedPosition('above');
      dispatch(setMovePosition({
        position: 'above',
        targetType: 'child',
        targetId: selectedChildId,
        targetIndex: selectedChildIndex,
        globalStepNumber: selectedGlobalStepNumber,
        sectionId: selectedSectionId,
        sectionIndex: selectedSectionIndex,
        stepId: selectedStepId,
        substepId: selectedSubstepId || null,
        targetStepIndex: selectedStepIndex
      }));
      if (onPositionSelected) {
        onPositionSelected('above', 'child', selectedChildId, selectedChildIndex, selectedGlobalStepNumber, selectedSectionId, selectedSectionIndex, selectedStepId, selectedSubstepId);
      }
    }
  };

  // Handle Move Below button
  const handleMoveBelow = () => {
    // Prevent action if loading
    if (moveIsLoading) return;
    
    if (selectedType === 'section') {
      dispatch(setMovePosition({
        position: 'below',
        targetType: 'section',
        targetId: selectedSectionId,
        targetIndex: selectedSectionIndex
      }));
      if (onPositionSelected) {
        onPositionSelected('below', 'section', selectedSectionId, selectedSectionIndex);
      }
    } else if (selectedType === 'step') {
      dispatch(setMovePosition({
        position: 'below',
        targetType: 'step',
        targetId: selectedStepId,
        targetIndex: selectedStepIndex,
        globalStepNumber: selectedGlobalStepNumber,
        sectionId: selectedSectionId,
        sectionIndex: selectedSectionIndex
      }));
      if (onPositionSelected) {
        onPositionSelected('below', 'step', selectedStepId, selectedStepIndex, selectedGlobalStepNumber, selectedSectionId, selectedSectionIndex);
      }
    } else if (selectedType === 'substep') {
      dispatch(setMovePosition({
        position: 'below',
        targetType: 'substep',
        targetId: selectedSubstepId,
        targetIndex: selectedSubstepIndex,
        globalStepNumber: selectedGlobalStepNumber,
        sectionId: selectedSectionId,
        sectionIndex: selectedSectionIndex,
        stepId: selectedStepId,
        substepLetter: selectedSubstepLetter
      }));
      if (onPositionSelected) {
        onPositionSelected('below', 'substep', selectedSubstepId, selectedSubstepIndex, selectedGlobalStepNumber, selectedSectionId, selectedSectionIndex);
      }
    } else if (selectedType === 'child' && ['attachment','video','rich-text','form','canva','canvas'].includes(moveNode?.nodeType)) {
      setSelectedPosition('below');
      dispatch(setMovePosition({
        position: 'below',
        targetType: 'child',
        targetId: selectedChildId,
        targetIndex: selectedChildIndex,
        globalStepNumber: selectedGlobalStepNumber,
        sectionId: selectedSectionId,
        sectionIndex: selectedSectionIndex,
        stepId: selectedStepId,
        substepId: selectedSubstepId || null,
        targetStepIndex: selectedStepIndex
      }));
      if (onPositionSelected) {
        onPositionSelected('below', 'child', selectedChildId, selectedChildIndex, selectedGlobalStepNumber, selectedSectionId, selectedSectionIndex, selectedStepId, selectedSubstepId);
      }
    }
  };

  // Determine which buttons to show
  const getButtons = () => {
    // Use "Paste" for copy operations, "Move" for move operations
    const actionPrefix = createCopy ? 'Paste' : 'Move';
    
    // Disable buttons if loading
    const buttonConfig = moveIsLoading ? { disabled: true } : {};
    
    if (moveNode?.nodeType === 'section') {
      if (selectedType === 'documentHeader') {
        return [{ label: `${actionPrefix} Into`, onClick: handleMoveInto, ...buttonConfig }];
      } else if (selectedType === 'section') {
        return [
          { label: `${actionPrefix} Above`, onClick: handleMoveAbove, ...buttonConfig },
          { label: `${actionPrefix} Below`, onClick: handleMoveBelow, ...buttonConfig }
        ];
      }
    } else if (moveNode?.nodeType === 'step') {
      if (selectedType === 'section') {
        return [{ label: `${actionPrefix} Into`, onClick: handleMoveInto, ...buttonConfig }];
      } else if (selectedType === 'step') {
        return [
          { label: `${actionPrefix} Above`, onClick: handleMoveAbove, ...buttonConfig },
          { label: `${actionPrefix} Below`, onClick: handleMoveBelow, ...buttonConfig }
        ];
      }
    } else if (moveNode?.nodeType === 'substep') {
      if (selectedType === 'step') {
        return [{ label: `${actionPrefix} Into`, onClick: handleMoveInto, ...buttonConfig }];
      } else if (selectedType === 'substep') {
        return [
          { label: `${actionPrefix} Above`, onClick: handleMoveAbove, ...buttonConfig },
          { label: `${actionPrefix} Below`, onClick: handleMoveBelow, ...buttonConfig }
        ];
      }
    } else if (['attachment','video','rich-text','form','canva','canvas'].includes(moveNode?.nodeType)) {
      if (selectedType === 'step') {
        return [{ label: `${actionPrefix} Into`, onClick: handleMoveInto, ...buttonConfig }];
      } else if (selectedType === 'substep') {
        return [{ label: `${actionPrefix} Into`, onClick: handleMoveInto, ...buttonConfig }];
      } else if (selectedType === 'child') {
        return [
          { label: `${actionPrefix} Above`, onClick: handleMoveAbove, ...buttonConfig },
          { label: `${actionPrefix} Below`, onClick: handleMoveBelow, ...buttonConfig }
        ];
      }
    }
    return [];
  };

  // Custom checkbox handler
  const handleNodeCheckbox = (nodeType, nodeData, context) => {
    if (nodeType === 'section') {
      // Sections should be selectable for section and step moves
      if (moveNode?.nodeType === 'section' || moveNode?.nodeType === 'step') {
        handleSectionCheckbox(nodeData.tree_node_id, context.sectionIndex);
      }
      // For substeps and child nodes, do nothing (sections not selectable)
    } else if (nodeType === 'step' && (moveNode?.nodeType === 'step' || ['attachment','video','rich-text','form','canva','canvas'].includes(moveNode?.nodeType) || moveNode?.nodeType === 'substep')) {
      handleStepCheckbox(
        nodeData.tree_node_id,
        context.stepIndex || 0,
        context.globalStepNumber,
        context.sectionId
      );
    } else if (nodeType === 'substep' && (moveNode?.nodeType === 'substep' || ['attachment','video','rich-text','form','canva','canvas'].includes(moveNode?.nodeType))) {
      handleSubstepCheckbox(
        nodeData.tree_node_id,
        context.substepIndex,
        context.substepLetter,
        context.globalStepNumber,
        context.stepId,
        context.sectionId
      );
    } else if (['attachment','video','rich-text','form','canva','canvas'].includes(nodeType) && ['attachment','video','rich-text','form','canva','canvas'].includes(moveNode?.nodeType)) {
      const childId = (nodeType === 'canva' || nodeType === 'canvas') 
        ? (nodeData.detail?.canvas_tree_node_id || nodeData.tree_node_id)
        : nodeData.tree_node_id;
      const normalizedType = (nodeType === 'canvas') ? 'canva' : nodeType;
      handleChildCheckbox(
        childId,
        normalizedType,
        context.nodeIndex,
        context.globalStepNumber,
        context.stepId,
        context.sectionId,
        context.substepId
      );
    }
  };

  // Get checkbox state
  const getCheckboxState = () => {
    if (moveNode?.nodeType === 'section') {
      // Always mark the source section (being moved) as checked
      const sourceSectionId = moveNode?.nodeId;
      const isSourceSection = sourceSectionId && selectionDocument.partId === moveDocument.partId;
      
      if (selectedType === 'section' && selectedSectionId) {
        return {
          currentNodeId: selectedSectionId,
          currentNodeType: 'section',
          allChildrenChecked: false,
          allChildrenDisabled: true,
          sourceNodeId: isSourceSection ? sourceSectionId : null, // Identify source section
        };
      }
      return {
        allChildrenDisabled: true,
        allChildrenChecked: false,
        sourceNodeId: isSourceSection ? sourceSectionId : null, // Always include source section ID
      };
    } else if (moveNode?.nodeType === 'step' || (['attachment','video','rich-text','form','canva','canvas'].includes(moveNode?.nodeType) && selectedType === 'step')) {
      // Always mark the source step (being moved) as checked
      const sourceStepId = moveNode?.nodeId;
      const isSourceStep = sourceStepId && selectionDocument.partId === moveDocument.partId && selectionDocument.revisionId === moveDocument.revisionId;
      
      if (selectedType === 'section' && selectedSectionId) {
        return {
          currentNodeId: selectedSectionId,
          currentNodeType: 'section',
          allChildrenChecked: false,
          allChildrenDisabled: false,
          sourceNodeId: isSourceStep ? sourceStepId : null, // Identify source step
        };
      } else if (selectedType === 'step' && selectedStepId) {
        return {
          currentNodeId: selectedStepId,
          currentNodeType: 'step',
          allChildrenChecked: false,
          allChildrenDisabled: false,
          sourceNodeId: isSourceStep ? sourceStepId : null, // Identify source step
        };
      }
      return {
        allChildrenDisabled: false,
        allChildrenChecked: false,
        sourceNodeId: isSourceStep ? sourceStepId : null, // Always include source step ID
      };
    } else if (moveNode?.nodeType === 'substep' || (['attachment','video','rich-text','form','canva','canvas'].includes(moveNode?.nodeType) && selectedType === 'substep')) {
      // Always mark the source substep (being moved) as checked
      const sourceSubstepId = moveNode?.nodeId;
      const isSourceSubstep = sourceSubstepId && selectionDocument.partId === moveDocument.partId && selectionDocument.revisionId === moveDocument.revisionId;
      
      if (selectedType === 'step' && selectedStepId) {
        return {
          currentNodeId: selectedStepId,
          currentNodeType: 'step',
          allChildrenChecked: false,
          allChildrenDisabled: false,
          sourceNodeId: isSourceSubstep ? sourceSubstepId : null, // Identify source substep
        };
      } else if (selectedType === 'substep' && selectedSubstepId) {
        return {
          currentNodeId: selectedSubstepId,
          currentNodeType: 'substep',
          allChildrenChecked: false,
          allChildrenDisabled: false,
          sourceNodeId: isSourceSubstep ? sourceSubstepId : null, // Identify source substep
        };
      }
      return {
        allChildrenDisabled: false,
        allChildrenChecked: false,
        sourceNodeId: isSourceSubstep ? sourceSubstepId : null, // Always include source substep ID
      };
    } else if (['attachment','video','rich-text','form','canva','canvas'].includes(moveNode?.nodeType)) {
      // Always mark the source child node (being moved) as checked
      const sourceChildNodeId = moveNode?.nodeId;
      const isSourceChildNode = sourceChildNodeId && selectionDocument.partId === moveDocument.partId && selectionDocument.revisionId === moveDocument.revisionId;
      
      if (selectedType === 'step' && selectedStepId) {
        return {
          currentNodeId: selectedStepId,
          currentNodeType: 'step',
          allChildrenChecked: false,
          allChildrenDisabled: false,
          sourceNodeId: isSourceChildNode ? sourceChildNodeId : null, // Identify source child node
        };
      } else if (selectedType === 'substep' && selectedSubstepId) {
        return {
          currentNodeId: selectedSubstepId,
          currentNodeType: 'substep',
          allChildrenChecked: false,
          allChildrenDisabled: false,
          sourceNodeId: isSourceChildNode ? sourceChildNodeId : null, // Identify source child node
        };
      } else if (selectedType === 'child' && selectedChildId) {
        const normalizedChildType = (selectedChildType === 'canvas') ? 'canva' : (selectedChildType || 'child');
        return {
          currentNodeId: selectedChildId,
          currentNodeType: normalizedChildType,
          allChildrenChecked: false,
          allChildrenDisabled: false,
          sourceNodeId: isSourceChildNode ? sourceChildNodeId : null, // Identify source child node
        };
      }
      return {
        allChildrenDisabled: false,
        allChildrenChecked: false,
        sourceNodeId: isSourceChildNode ? sourceChildNodeId : null, // Always include source child node ID
      };
    }
    return {
      allChildrenDisabled: true,
      allChildrenChecked: false,
    };
  };

  if (!selectionDocument || !moveNode || !moveDocument) {
    return <div>Missing move document or move node data.</div>;
  }

  // Helper function to calculate global node number for a child node
  const calculateGlobalNodeNumberByType = (targetChildId, targetChildType) => {
    if (!moveOriginalData || !targetChildId || !targetChildType) return null;
    
    const nodeType = targetChildType === 'canvas' ? 'canva' : targetChildType;
    let counter = 0;

    // Sort all sections by sibling_order
    const compareSiblingOrders = (a, b) => {
      const intA = parseInt(a, 10);
      const intB = parseInt(b, 10);
      return intA - intB;
    };

    const sortedSections = [...moveOriginalData].sort((a, b) => {
      const orderA = a.sibling_order || a.procedure_tree?.sibling_order;
      const orderB = b.sibling_order || b.procedure_tree?.sibling_order;
      const orderCompare = compareSiblingOrders(orderA, orderB);
      if (orderCompare !== 0) return orderCompare;
      const dateA = new Date(a.created_at);
      const dateB = new Date(b.created_at);
      return dateB - dateA;
    });

    // Count all nodes of this type before this one across all sections, steps, and substeps
    for (let i = 0; i < sortedSections.length; i++) {
      const section = sortedSections[i];

      for (let j = 0; j < (section.steps?.length || 0); j++) {
        const step = section.steps[j];

        // Count step-level children
        const stepChildren = step.children?.filter((c) => c.type === nodeType) || [];

        // Check if this is our target child in a step
        const targetChildInStep = stepChildren.find((c) => {
          const childId = c.detail?.canvas_tree_node_id || c.tree_node_id;
          return childId === targetChildId || c.tree_node_id === targetChildId;
        });
        if (targetChildInStep) {
          // Add children before this one in the step
          const childIndex = stepChildren.findIndex((c) => {
            const childId = c.detail?.canvas_tree_node_id || c.tree_node_id;
            return childId === targetChildId || c.tree_node_id === targetChildId;
          });
          counter += childIndex !== -1 ? childIndex : 0;
          return counter + 1;
        } else {
          counter += stepChildren.length;
        }

        // Count substep-level children
        for (let k = 0; k < (step.substeps?.length || 0); k++) {
          const substep = step.substeps[k];
          const substepChildren = substep.children?.filter((c) => c.type === nodeType) || [];

          // Check if this is our target child in a substep
          const targetChildInSubstep = substepChildren.find((c) => {
            const childId = c.detail?.canvas_tree_node_id || c.tree_node_id;
            return childId === targetChildId || c.tree_node_id === targetChildId;
          });
          if (targetChildInSubstep) {
            // Add children before this one in the substep
            const childIndex = substepChildren.findIndex((c) => {
              const childId = c.detail?.canvas_tree_node_id || c.tree_node_id;
              return childId === targetChildId || c.tree_node_id === targetChildId;
            });
            counter += childIndex !== -1 ? childIndex : 0;
            return counter + 1;
          } else {
            counter += substepChildren.length;
          }
        }
      }
    }
    return null;
  };

  // Helper to render underlined text
  const renderUnderlined = (text) => {
    return <span style={{ textDecoration: 'underline' }}>{text}</span>;
  };

  const getTitle = () => {
    // Show "Copying" or "Moving" based on createCopy checkbox
    const action = createCopy ? 'Copying' : 'Moving';
    const nodeTitle = moveNode.nodeTitle || moveNode.nodeType?.charAt(0).toUpperCase() + moveNode.nodeType?.slice(1) || 'Node';
    
    // If no selection made, just show action and source node
    if (!selectedType) {
      return <>{action} {renderUnderlined(nodeTitle)}</>;
    }

    // Build message based on selection type
    if (selectedType === 'documentHeader') {
      // For section INTO document: "Moving Section 1 to {type} {partnumber}"
      if (moveNode.nodeType === 'section') {
        const docType = selectionDocument?.type || 'Document';
        const partNumber = selectionDocument?.partNumber || '';
        return <>{action} {renderUnderlined(nodeTitle)} to {docType} {partNumber}</>;
      }
      return <>{action} {renderUnderlined(nodeTitle)}</>;
    }

    if (selectedType === 'section') {
      if (moveNode.nodeType === 'section') {
        // Section to section: "Moving Section 1 to Section 2"
        const targetSectionTitle = selectedSectionIndex !== null && selectedSectionIndex !== undefined
          ? `Section ${selectedSectionIndex + 1}`
          : 'Section';
        return <>{action} {renderUnderlined(nodeTitle)} to {renderUnderlined(targetSectionTitle)}</>;
      } else if (moveNode.nodeType === 'step') {
        // Step INTO section: "Moving Step 1 into Section 2"
        const targetSectionTitle = selectedSectionIndex !== null && selectedSectionIndex !== undefined
          ? `Section ${selectedSectionIndex + 1}`
          : 'Section';
        return <>{action} {renderUnderlined(nodeTitle)} into {renderUnderlined(targetSectionTitle)}</>;
      }
      return <>{action} {renderUnderlined(nodeTitle)}</>;
    }

    if (selectedType === 'step') {
      if (moveNode.nodeType === 'step') {
        // Step to step: "Moving Step 1 to Step 2"
        const targetStepTitle = selectedGlobalStepNumber !== null && selectedGlobalStepNumber !== undefined
          ? `Step ${selectedGlobalStepNumber}`
          : 'Step';
        return <>{action} {renderUnderlined(nodeTitle)} to {renderUnderlined(targetStepTitle)}</>;
      } else if (moveNode.nodeType === 'substep') {
        // Substep INTO step: "Moving Substep 1A into Step 1"
        const targetStepTitle = selectedGlobalStepNumber !== null && selectedGlobalStepNumber !== undefined
          ? `Step ${selectedGlobalStepNumber}`
          : 'Step';
        return <>{action} {renderUnderlined(nodeTitle)} into {renderUnderlined(targetStepTitle)}</>;
      } else if (['attachment', 'video', 'rich-text', 'form', 'canva', 'canvas'].includes(moveNode.nodeType)) {
        // Child INTO step: "Moving Attachment 1 into Step 1"
        const targetStepTitle = selectedGlobalStepNumber !== null && selectedGlobalStepNumber !== undefined
          ? `Step ${selectedGlobalStepNumber}`
          : 'Step';
        return <>{action} {renderUnderlined(nodeTitle)} into {renderUnderlined(targetStepTitle)}</>;
      }
      return <>{action} {renderUnderlined(nodeTitle)}</>;
    }

    if (selectedType === 'substep') {
      if (moveNode.nodeType === 'substep') {
        // Substep to substep: "Moving Substep 1A to Substep 1B"
        let targetSubstepTitle = '';
        if (selectedGlobalStepNumber !== null && selectedGlobalStepNumber !== undefined && selectedSubstepLetter) {
          targetSubstepTitle = `Substep ${selectedGlobalStepNumber}${selectedSubstepLetter}`;
        } else {
          targetSubstepTitle = 'Substep';
        }
        return <>{action} {renderUnderlined(nodeTitle)} to {renderUnderlined(targetSubstepTitle)}</>;
      } else if (['attachment', 'video', 'rich-text', 'form', 'canva', 'canvas'].includes(moveNode.nodeType)) {
        // Child INTO substep: "Moving Attachment 1 into Substep 1A"
        let targetSubstepTitle = '';
        if (selectedGlobalStepNumber !== null && selectedGlobalStepNumber !== undefined && selectedSubstepLetter) {
          targetSubstepTitle = `Substep ${selectedGlobalStepNumber}${selectedSubstepLetter}`;
        } else {
          targetSubstepTitle = 'Substep';
        }
        return <>{action} {renderUnderlined(nodeTitle)} into {renderUnderlined(targetSubstepTitle)}</>;
      }
      return <>{action} {renderUnderlined(nodeTitle)}</>;
    }

    if (selectedType === 'child') {
      // Child to child: "Moving Attachment 1 below Canvas 1" or "Moving Attachment 1 above Canvas 2"
      const childTypeLabel = selectedChildType === 'rich-text' ? 'Rich Text' :
                           selectedChildType === 'canva' || selectedChildType === 'canvas' ? 'Canvas' :
                           selectedChildType?.charAt(0).toUpperCase() + selectedChildType?.slice(1) || 'Child';
      
      // Calculate global node number for the target child
      const globalNodeNumber = calculateGlobalNodeNumberByType(selectedChildId, selectedChildType);
      const targetChildTitle = globalNodeNumber !== null
        ? `${childTypeLabel} ${globalNodeNumber}`
        : (selectedChildIndex !== null && selectedChildIndex !== undefined
          ? `${childTypeLabel} ${selectedChildIndex + 1}`
          : childTypeLabel);
      
      // Show position (above/below) if selected
      if (selectedPosition === 'above' || selectedPosition === 'below') {
        return <>{action} {renderUnderlined(nodeTitle)} {selectedPosition} {renderUnderlined(targetChildTitle)}</>;
      }
      
      // If no position button clicked yet, just show the target
      return <>{action} {renderUnderlined(nodeTitle)} to {renderUnderlined(targetChildTitle)}</>;
    }

    return <>{action} {renderUnderlined(nodeTitle)}</>;
  };

  const title = getTitle();

  return (
    <div>
      <VersionHistoryCopyAppBar
        title={title}
        subheading=""
        buttons={getButtons()}
        buttonsBeforeTitle={[]}
        showBackButton={false}
        showProfileButton={false}
        showTablesDropdown={false}
        showCancelButton={true}
        onCancel={onCancel}
        showViewToggle={false}
        showCreateCopyCheckbox={true}
        createCopy={createCopy}
        onCreateCopyChange={(e) => onCreateCopyChange(e.target.checked)}
      />

      <div style={{ padding: '20px' }}>
        {/* DocumentHeader - with checkbox for section move */}
        <DocumentHeader
          type={selectionDocument.type}
          partNumber={selectionDocument.partNumber}
          revision={selectionDocument.revision}
          revisionId={selectionDocument.revisionId}
          partId={selectionDocument.partId}
          // description={selectionDocument.description}
          description={false}
          status={selectionDocument.status}
          currentRevision={{}}
          showCheckbox={moveNode?.nodeType === 'section'}
          showVersionInfo={false}
          isCheckboxEnabled={moveNode?.nodeType === 'section'}
          isCheckboxChecked={isDocumentHeaderChecked}
          checkboxLabelColor={moveNode?.nodeType === 'section' ? "rgb(0, 102, 204)" : undefined}
          onCheckboxClick={handleDocumentHeaderCheckbox}
          
        />

        {/* Render all nodes */}
        <div style={{ marginTop: '20px' }}>
          <MoveNodesRenderer
            preloadedData={moveOriginalData || []}
            customOnNavigate={handleNodeCheckbox}
            checkboxState={getCheckboxState()}
            moveNodeType={moveNode?.nodeType}
            checkboxLabelColor="rgb(0, 102, 204)"
          />
        </div>
      </div>

      {/* Loading overlay when computing preview */}
      {moveIsLoading && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(255,255,255,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <Loader text={createCopy ? "Preparing copy..." : "Preparing move..."} />
        </div>
      )}
    </div>
  );
};

export default MovePositionSelector;

